import { useEffect, useState, type ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkspaceMember } from '@molt/shared';

import { Avatar, Button, EmptyState, Notice, Skeleton, profileName } from '@/components';
import {
  getWorkspaceActivity,
  mergeWorkspaceActivityFeeds,
  workspaceActivityQueryOptions,
  type ActivityFilter,
} from '@/lib/activity';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { ActivityEntryItem } from '../card-back/ActivityEntryItem';

export interface WorkspaceActivityPageProps {
  readonly workspaceId: string;
  readonly members: readonly WorkspaceMember[];
}

const PAGE_SIZE = 25;

export function WorkspaceActivityPage({
  workspaceId,
  members,
}: WorkspaceActivityPageProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const selectedMember =
    memberId === null ? null : (members.find((member) => member.id === memberId) ?? null);
  const effectiveMemberId = selectedMember?.id ?? null;
  const activity = useQuery(
    workspaceActivityQueryOptions(workspaceId, filter, effectiveMemberId, PAGE_SIZE),
  );
  const data = activity.data;

  useEffect(() => {
    if (memberId !== null && selectedMember === null) {
      setMemberId(null);
    }
  }, [memberId, selectedMember]);

  async function loadMore() {
    if (data?.nextCursor === null || data?.nextCursor === undefined) return;
    setLoadingMore(true);
    try {
      const next = await getWorkspaceActivity(workspaceId, {
        filter,
        cursor: data.nextCursor,
        limit: PAGE_SIZE,
        ...(effectiveMemberId === null ? {} : { memberId: effectiveMemberId }),
      });
      queryClient.setQueryData(
        queryKeys.workspaces.activity(workspaceId, filter, effectiveMemberId),
        mergeWorkspaceActivityFeeds(data, next),
      );
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 py-8">
      <header className="flex flex-col gap-2">
        <p className="text-sm font-medium text-ink-muted">
          {t('workspaceSettings.activity.eyebrow')}
        </p>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
          {t('workspaceSettings.activity.title')}
        </h1>
      </header>

      <section aria-labelledby="workspace-activity-member-filter" className="space-y-3">
        <div className="flex flex-col gap-1">
          <h2
            id="workspace-activity-member-filter"
            className="text-base font-semibold text-ink"
          >
            {t('workspaceSettings.activity.memberFilterHeading')}
          </h2>
          <p className="text-sm text-ink-muted">
            {t('workspaceSettings.activity.memberFilterDescription')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="toggle"
            pressed={effectiveMemberId === null}
            onClick={() => setMemberId(null)}
          >
            {t('workspaceSettings.activity.allMembers')}
          </Button>
          {members.map((member) => {
            const selected = member.id === effectiveMemberId;
            const name = profileName(member);
            return (
              <button
                key={member.id}
                type="button"
                aria-label={t('workspaceSettings.activity.memberFilterControl', { name })}
                aria-pressed={selected}
                onClick={() => setMemberId(selected ? null : member.id)}
                /*
                  `FE-66.2`, closing the residual `FE-04.17` left beside this
                  call site. `FR-COLLAB-021`.

                  ── What was wrong, and it was a 1.4.11 failure ──────────────
                  This chip's selected arm used to be `border-accent
                  bg-accent-soft` and its unselected arm `border-border-strong
                  bg-surface-raised hover:border-accent`. Read those two
                  literally: an unselected chip under the pointer wears the
                  SELECTED chip's own border, so while a neighbour is hovered
                  the only thing separating the two states visually is the fill
                  — and the fill was never a signal.

                  Measured (oklch -> oklab -> linear sRGB, gamut-clamped,
                  WCAG relative luminance; the same arithmetic
                  `styles.tokens.test.ts` runs, and it runs it on these pairs):

                    border-accent vs border-border-strong  1.60:1 / 1.68:1
                    bg-accent-soft vs bg-surface-raised    1.07:1 / 1.10:1

                  Both under 1.4.11's 3:1, light / dark. `aria-pressed` was
                  carrying the state alone, which is why nothing was ever red:
                  the non-visual half was correct the whole time.

                  ⚠ `FE-04.17` did NOT cause this and is not reopened. Before
                  that token the fill was TRANSPARENT over `--color-surface`,
                  which sits 1.08:1 / 1.15:1 from `bg-surface-raised` — a
                  non-signal before, a non-signal after, by a hundredth.

                  ── The remedy, and why this one ────────────────────────────
                  The solid treatment `Button`'s `toggle` variant already uses
                  when pressed (`aria-pressed:border-accent aria-pressed:bg-accent
                  aria-pressed:text-accent-ink`). Measured after:

                    bg-accent vs a neighbour's bg-surface-raised
                                                          4.98:1 / 5.17:1  PASS
                    bg-accent vs the page --color-surface  5.37:1 / 5.93:1  PASS
                    text-accent-ink on bg-accent          5.43:1 / 6.52:1  AA
                    text-ink on bg-surface-raised        13.46:1 / 11.70:1 AA

                  3.12x / 3.08x the boundary this chip used to have, and — the
                  point — the signal is now the FILL, which a hovered neighbour
                  cannot borrow. `hover:border-accent` therefore stays: it is a
                  hover affordance again rather than an impersonation.

                  The TREATMENT is adopted, not the `Button` component.
                  `size='sm'` is `h-7`, and this chip carries an `Avatar
                  size='sm'` (`size-7`, 28px) plus a name — no padding left;
                  `size='md'` is `h-9 px-3 text-card` and changes the rhythm and
                  the type size of a row that already sits beside an `h-7`
                  toggle. The box here is byte-identical to what it was.

                  ── `text-ink` is deliberately NOT in the base class ─────────
                  It used to be, which made the selected arm's `text-accent-ink`
                  depend on `tailwind-merge` dropping it. That is the exact
                  shape of the `--text-card` defect `CLAUDE.md` records: a
                  colour that dies inside `cn` past a green class assertion.
                  Both arms now state their own foreground positively, so no
                  merge resolution is load-bearing — and the sibling test reads
                  the RENDERED className, which is the only place a `cn` drop is
                  visible.

                  ── The focus ring still reads as a ring ────────────────────
                  Worth stating, because accent-outline over accent-border over
                  accent-fill would otherwise be one blob. `styles.css`'s global
                  `:focus-visible` sets `outline-offset: 2px` (pinned by
                  `board-contrast.test.ts`), so `--color-surface` shows through
                  between the border and the outline at 5.37:1 / 5.93:1. No
                  `outline-offset` utility is added here; adding one would
                  restate the global rule and invite someone to tune it.
                */
                className={cn(
                  'inline-flex h-9 items-center gap-2 rounded-card border px-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
                  selected
                    ? 'border-accent bg-accent text-accent-ink'
                    : 'border-border-strong bg-surface-raised text-ink hover:border-accent',
                )}
              >
                <Avatar profile={member} size="sm" decorative />
                <span className="max-w-32 truncate">{name}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section
        aria-label={t('workspaceSettings.activity.feedLabel')}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="toggle"
              pressed={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              {t('workspaceSettings.activity.filterAll')}
            </Button>
            <Button
              size="sm"
              variant="toggle"
              pressed={filter === 'comments'}
              onClick={() => setFilter('comments')}
            >
              {t('workspaceSettings.activity.filterComments')}
            </Button>
          </div>
          {selectedMember === null ? null : (
            <p className="text-sm text-ink-muted">
              {t('workspaceSettings.activity.showingMember', {
                name: profileName(selectedMember),
              })}
            </p>
          )}
        </div>

        {activity.isLoading ? (
          <Skeleton shape="block" count={3} />
        ) : activity.isError || data === undefined ? (
          <Notice
            tone="bad"
            title={t('workspaceSettings.activity.loadFailedTitle')}
            body={t('workspaceSettings.activity.loadFailedBody')}
            action={
              <Button size="sm" onClick={() => void activity.refetch()}>
                {t('workspaceSettings.activity.retry')}
              </Button>
            }
          />
        ) : data.entries.length === 0 ? (
          <EmptyState
            tone="neutral"
            size="inline"
            headingLevel={2}
            title={
              filter === 'comments'
                ? t('workspaceSettings.activity.emptyCommentsTitle')
                : t('workspaceSettings.activity.emptyActivityTitle')
            }
            body={
              selectedMember === null
                ? t('workspaceSettings.activity.emptyBody')
                : t('workspaceSettings.activity.emptyMemberBody', {
                    name: profileName(selectedMember),
                  })
            }
          />
        ) : (
          <>
            <div>
              {data.entries.map((entry) => (
                <ActivityEntryItem key={entry.id} entry={entry} showCard showBoard />
              ))}
            </div>
            {data.nextCursor !== null ? (
              <button
                type="button"
                className="self-start rounded-card px-2 py-1 text-sm font-medium text-accent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {loadingMore
                  ? t('workspaceSettings.activity.loadingMore')
                  : t('workspaceSettings.activity.loadMore')}
              </button>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
